/*
Copyright 2026 New Vector Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import { type FC, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { type Room } from "matrix-js-sdk";
import { Button, Text } from "@vector-im/compound-web";
import {
  LinkIcon,
  CheckIcon,
  InfoIcon,
} from "@vector-im/compound-design-tokens/assets/web/icons";
import copy from "copy-to-clipboard";

import { Modal } from "../Modal";
import { getAbsoluteRoomUrl, getRelativeRoomUrl } from "../utils/matrix";
import { useRoomEncryptionSystem } from "../e2ee/sharedKeyManagement";
import {
  getHomeserverAssociatedDomain,
  isKnownHomeserver,
} from "../knownHomeserversMap.ts";
import styles from "./PublicRoomShareModal.module.css";

interface Props {
  room: Room;
  open: boolean;
  onDismiss: () => void;
}

interface CopyLinkButtonProps {
  label: string;
  link: string;
}

/**
 * Возвращает ссылку на публичную комнату в веб-клиенте для сотрудников организации.
 */
function getEmployeeRoomUrl(room: Room): string {
  const roomIdOrAlias =
    room.getCanonicalAlias?.() ?? room.getAltAliases?.()[0] ?? room.roomId;
  const homeserver = room.client.getDomain?.() ?? "";

  if (homeserver && isKnownHomeserver(homeserver)) {
    const webDomain = getHomeserverAssociatedDomain(homeserver, "web");
    return `https://${webDomain}/#/room/${roomIdOrAlias}`;
  }

  return `https://matrix.to/#/${roomIdOrAlias}`;
}

/**
 * Стандартная кнопка копирования со временным состоянием успеха.
 */
const CopyLinkButton: FC<CopyLinkButtonProps> = ({ label, link }) => {
  const { t } = useTranslation();
  const timeoutIdRef = useRef<number | undefined>(undefined);
  const [isCopied, setIsCopied] = useState(false);

  useEffect((): (() => void) => {
    return (): void => clearTimeout(timeoutIdRef.current);
  }, []);

  const onClick = (): void => {
    clearTimeout(timeoutIdRef.current);

    if (!copy(link)) return;

    setIsCopied(true);
    timeoutIdRef.current = window.setTimeout(() => setIsCopied(false), 2000);
  };

  return (
    <Button
      className={styles.copyButton}
      Icon={isCopied ? CheckIcon : LinkIcon}
      onClick={onClick}
    >
      {isCopied ? t("public_room_share_modal.link_copied_action") : label}
    </Button>
  );
};

/**
 * Модалка шаринга публичной комнаты с отдельными ссылками для сотрудников и гостей.
 */
export const PublicRoomShareModal: FC<Props> = ({ room, open, onDismiss }) => {
  const { t } = useTranslation();
  const e2eeSystem = useRoomEncryptionSystem(room.roomId);
  const employeeUrl = useMemo(() => getEmployeeRoomUrl(room), [room]);

  const guestUrl = useMemo(() => {
    const elementCallBaseUrl = getHomeserverAssociatedDomain(
      room.client.getDomain() ?? "",
      "call",
    );
    if (elementCallBaseUrl) {
      const relativePath = getRelativeRoomUrl(
        room.roomId,
        e2eeSystem,
        room.name,
      );
      return `https://${elementCallBaseUrl}${relativePath}`;
    }

    return getAbsoluteRoomUrl(room.roomId, e2eeSystem, room.name);
  }, [e2eeSystem, room]);

  return (
    <Modal
      title={t("public_room_share_modal.title")}
      open={open}
      onDismiss={onDismiss}
      className={styles.modal}
    >
      <div className={styles.content}>
        <Text className={styles.description} size="md">
          {t("public_room_share_modal.description")}
        </Text>

        <div className={styles.sections}>
          <section className={styles.section}>
            <div className={styles.sectionText}>
              <Text
                as="h3"
                className={styles.sectionTitle}
                size="md"
                weight="semibold"
              >
                {t("public_room_share_modal.employee_title")}
              </Text>
              <Text className={styles.sectionDescription} size="md">
                {t("public_room_share_modal.employee_description")}
              </Text>
            </div>

            <div className={styles.notice}>
              <InfoIcon className={styles.noticeIcon} />
              <Text className={styles.noticeText} size="md">
                {t("public_room_share_modal.employee_notice")}
              </Text>
            </div>

            <CopyLinkButton
              label={t("public_room_share_modal.employee_action")}
              link={employeeUrl}
            />
          </section>

          <section className={styles.section}>
            <div className={styles.sectionText}>
              <Text
                as="h3"
                className={styles.sectionTitle}
                size="md"
                weight="semibold"
              >
                {t("public_room_share_modal.guest_title")}
              </Text>
              <Text className={styles.sectionDescription} size="md">
                {t("public_room_share_modal.guest_description")}
              </Text>
            </div>

            <CopyLinkButton
              label={t("public_room_share_modal.guest_action")}
              link={guestUrl}
            />
          </section>
        </div>
      </div>
    </Modal>
  );
};
